import { Box } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { TeamMembershipOut } from '@repo/api-codegen';
import {
	apiQueryKey,
	useApiDeleteMembership,
	useApiUpdateMembership,
} from '@repo/api-codegen';
import { Button, Text } from '@repo/foundations';
import { useState } from 'react';
import { queryClient, useWorkspace } from '../../api';
import {
	useApproveTeamAccessRequest,
	useRejectTeamAccessRequest,
} from '../../api/hooks/team/accessRequests';
import { CustomSelector } from '../CustomSelector';

type Props = {
	membership: TeamMembershipOut;
	disabled?: boolean;
};

const toValue = (membership: TeamMembershipOut) => {
	if (!membership.read) {
		return 'pending';
	}
	if (!membership.write) {
		return 'read';
	}
	return 'write';
};

const text = (membership: TeamMembershipOut, value: string) => {
	if (value === 'delete') {
		return 'Remove from team';
	}
	if (value === 'approve') {
		return 'Approve request';
	}
	if (value === 'reject') {
		return 'Reject request';
	}
	if (value === 'pending') {
		return 'Pending approval';
	}

	if (value === 'read') {
		return 'Can read';
	}

	if (membership.user) {
		return 'Can edit';
	}
	return 'Editors can edit';
};

export function MembershipSelector({ membership, disabled }: Props) {
	const { workspace } = useWorkspace();
	const [permission, setPermission] = useState<string>(toValue(membership));

	const { mutateAsync: updateTeamMembership } = useApiUpdateMembership({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: apiQueryKey('auth/teams/{team_id}', {
					team_id: membership.team_id,
				}),
			});
		},
		onError: () => {
			showNotification({
				title: 'Failed to update team member',
				message: 'Please check permissions and try again',
				color: 'red',
			});
		},
	});

	const { mutateAsync: deleteTeamMembership } = useApiDeleteMembership({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: apiQueryKey('auth/teams/{team_id}', {
					team_id: membership.team_id,
				}),
			});
		},
	});
	const { mutateAsync: approveTeamAccessRequest } =
		useApproveTeamAccessRequest();
	const { mutateAsync: rejectTeamAccessRequest } = useRejectTeamAccessRequest();

	const onSelect = async (value: string) => {
		if (!membership.read && value === 'approve') {
			await approveTeamAccessRequest({
				teamId: membership.team_id,
				membershipId: membership.id,
			});
			queryClient.invalidateQueries({
				queryKey: apiQueryKey('auth/teams/{team_id}/memberships', {
					team_id: membership.team_id,
				}),
			});
			setPermission('read');
			return;
		}
		if (!membership.read && value === 'reject') {
			await rejectTeamAccessRequest({
				teamId: membership.team_id,
				membershipId: membership.id,
			});
			queryClient.invalidateQueries({
				queryKey: apiQueryKey('auth/teams/{team_id}/memberships', {
					team_id: membership.team_id,
				}),
			});
			return;
		}
		if (value === 'delete') {
			await deleteTeamMembership({
				pathParams: {
					teamId: membership.team_id,
					membershipId: membership.id,
				},
			});
		} else {
			await updateTeamMembership({
				pathParams: {
					teamId: membership.team_id,
					membershipId: membership.id,
				},
				body: {
					read: true,
					write: value === 'write',
				},
			});
			setPermission(value);
		}
	};

	if (workspace?.migrated_permission_v2) {
		return (
			<Button
				size="sm"
				variant="default"
				color="red"
				onClick={() => onSelect('delete')}
			>
				Remove
			</Button>
		);
	}

	if (!membership.read) {
		return (
			<CustomSelector
				disabled={disabled}
				menuProps={{
					width: 'target',
					position: 'bottom-start',
					withinPortal: true,
				}}
				items={[
					{
						value: 'pending',
						disabled: true,
					},
					{
						value: 'approve',
					},
					{
						value: 'reject',
					},
				]}
				renderItem={(item) => (
					<Box px={8} py={6}>
						<Text
							size="sm"
							color={
								item.value === 'reject'
									? 'text/critical/default'
									: 'text/primary/default'
							}
						>
							{text(membership, item.value)}
						</Text>
					</Box>
				)}
				value="pending"
				onChange={onSelect}
			/>
		);
	}

	return (
		<CustomSelector
			disabled={disabled}
			menuProps={{
				width: 'target',
				position: 'bottom-start',
				withinPortal: true,
			}}
			items={[
				{
					value: 'read',
					disabled: membership.user?.role === 'admin',
				},
				{
					value: 'write',
					disabled:
						membership.user?.role === 'viewer' ||
						membership.user?.role === 'guest',
				},
				{
					value: 'delete',
				},
			]}
			renderItem={(item) => {
				const getTextColor = () => {
					if (item.disabled) return 'text/secondary/default';
					if (item.value === 'delete') return 'text/critical/default';
					return 'text/primary/default';
				};

				return (
					<Box px={8} py={6}>
						<Text size="sm" color={getTextColor()}>
							{text(membership, item.value)}
						</Text>
					</Box>
				);
			}}
			value={permission}
			onChange={onSelect}
		/>
	);
}
